import React, { useContext, useEffect, useRef, useState } from 'react';
import styles from "./ShareAssessmentForm.module.scss";
import { Field, Formik, FormikProps } from 'formik';
import { Input } from '../common/Input';
import * as yup from 'yup';
import { ButtonPageLayout } from '../common/ButtonPageLayout';
import { UserContext } from '../../context/UserContext';

interface ShareAssessmentData {
    name: string;
    emailAddress: string;
    language: string;
}

type AssessmentInviteProps = {
  isOpen: boolean;
  onToggle: Function;
  onClick: Function;
  sendingSuccessful: Boolean
  errorFound: Boolean
  row: {
     venture: String
  }
};

const validationSchema = yup.object().shape({
    name: yup.string().required(),
    emailAddress: yup.string().email().required(),
    language: yup.string().oneOf(['nl', 'en']),
});

const letterNL = `
Beste [naam],

Op basis van jouw antwoorden hebben wij [venture] geanalyseerd. Bijgaand vind je een rapport met de resultaten en inzichten over de volgende groeistappen. 

* Voor de rapportage: [link scan]. 
* Voor de vragenlijst met antwoorden: [link questionnaire].
 
Mocht je vragen hebben over de vragenlijst of het rapport, schroom dan niet om contact met mij op te nemen op [email begeleider].

Hartelijke groet,

[begeleider]
`;

const letterEN = `
Dear [naam],
 
Based on your answers, we analyzed [venture]. Attached you will find a report with the results and insights about the next growth steps.

* For the report: [link scan]. 
* For the questionnaire with answers: [link questionnaire].

If you have any questions about the questionnaire or the report, please do not hesitate to contact me at [email begeleider].

[begeleider]
`

export const ShareAssessmentForm = ({
  isOpen,
  onToggle,
  onClick,
  sendingSuccessful,
  errorFound,
  row
}: AssessmentInviteProps) => {
    const initialFormValues = {
        name: "",
        emailAddress: "",
        language: "en",
    };

  const formikRef = useRef<FormikProps<AssessmentInviteProps>>(null);
  const [message, setMessage] = useState<String | null>(null);
  const [nameValidation, setNameValidation] = useState(false);
  const [formData, setFormData] = useState<ShareAssessmentData>(initialFormValues)
  const [formValid, setFormValid] = useState(false)
  const userContext = useContext(UserContext)

  // @ts-ignore
    const submitForm = ( values: {name: string, emailAddress: string, language: string}, row ) => {
      const { name, emailAddress, language } = values;

        let letter = ''
        if(language === 'nl') {
            letter = letterNL
        } else {
            letter = letterEN
        }

      let filledInLetter = letter.replace('[naam]', name)
      // @ts-ignore
      filledInLetter = filledInLetter.replaceAll('[venture]', row.companyName)
      filledInLetter = filledInLetter.replace('[founder]', row.founder)
      filledInLetter = filledInLetter.replaceAll('[assessment_name]', row.scanType)
      filledInLetter = filledInLetter.replace('[date]', row.assessmentDate)
      filledInLetter = filledInLetter.replace('[begeleider]', userContext.name)
      filledInLetter = filledInLetter.replace('[email begeleider]', userContext.emailAddress)

      setMessage(filledInLetter)
      setFormValid(!formValid)
      setFormData({
          name,
          emailAddress,
          language
      })
  }

  const closeModal = () => {
      formikRef.current?.resetForm()
      setFormData(initialFormValues)
      setFormValid(false)
      onToggle()
  }

    useEffect(() => {
        if(isOpen) {
            document.getElementById('assessment-invite-name')?.focus()
            setNameValidation(true)
        } else {
            setNameValidation(false)
        }
    }, [isOpen]);


    return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <p className={styles.name}>Share</p>
        <button className={styles.close} onClick={closeModal}>
          X
        </button>
      </div>
      <h2 className={styles.title}>{`Share scan with`}</h2>
        {!formValid && (
        <section>
        <Formik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={formData}
            onSubmit={(values: {name: string, emailAddress: string, language: string}) => {
                submitForm(values, row)
            }}
            // @ts-ignore
            innerRef={formikRef}
        >
            {({ values, errors, touched, handleChange, handleSubmit, handleBlur, isValid }) => (
                <form className={styles.formContainer} onSubmit={handleSubmit}>
                    <div className={styles.inputContainer}>
                        {errors.name && touched.name ? (
                            <span className={styles.error}>Name is required</span>
                        ) : null}
                        <Field
                            id="assessment-invite-name"
                            name='name'
                            as={Input}
                            type='text'
                            placeholder='Name'
                            onChange={handleChange}
                            value={values.name}
                            autoFocus
                            onBlur={(e: React.FocusEvent<any>) => {
                                if(nameValidation) {
                                    handleBlur(e)
                                }
                            }}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        {errors.emailAddress && touched.emailAddress ? (
                            errors.emailAddress.includes('valid')
                                ? <span className={styles.error}>Email format is incorrect</span>
                                : <span className={styles.error}>Email address is required</span>
                        ) : null}
                        <Field
                            name='emailAddress'
                            as={Input}
                            type='text'
                            placeholder='Email address'
                            onChange={handleChange}
                            value={values.emailAddress}
                        />
                    </div>
                    <div className={styles.selectContainer}>
                        <Field
                            className={styles.select}
                            name='language'
                            as="select"
                            placeholder='Language'
                            onChange={handleChange}
                            value={values.language}
                        >
                            <option value="nl">Dutch</option>
                            <option value="en">English</option>
                        </Field>
                    </div>
                    <div className={styles.modalButtons}>
                        <ButtonPageLayout type='submit' disabled={!isValid}>Next</ButtonPageLayout>
                    </div>
                </form>
            )}
        </Formik>
      </section>
    )}
    {formValid && message !== null && (
        <section>
          <textarea
            className={styles.textField}
            value={typeof message === 'string' ? message : '' }
            onChange={(e) => setMessage(e.target.value)}
          ></textarea>
                <section className={styles.modalButtons}>
                    <button
                        className={styles.send}
                        onClick={() => {
                            onClick(formData, message)
                            setTimeout(() => {
                                formikRef.current?.resetForm()
                                setFormData(initialFormValues)
                                setFormValid(false)
                            }, 2000)
                        }}
                    >{`Send`}</button>
                    <button
                    className={styles.cancel}
                    onClick={closeModal}
                    >{`Cancel`}</button>
                </section>
            {sendingSuccessful && (
                <span className={styles.sendSuccessful}>Mail sent succesfully</span>
            )}
            {errorFound && (
                <span className={styles.sendFailed}>Oops, something went wrong with sharing the assessment, don't worry, we're notified of the issue </span>
            )}
        </section>
    )}
    </div>
  );
};
